import { z } from 'zod';

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

export const downloadBody = z.object({
  type: z.enum(['workspace', 'agent_instructions']),
  agent_id: z.string().uuid().optional(),
  organization_name: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

export const uploadQuery = z.object({
  type: z.enum(['agent_instructions']),
  agent_id: z.string().uuid(),
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Sign URL
// ---------------------------------------------------------------------------

export const signUrlBody = z.object({
  bucket: z.string(),
  filePath: z.string(),
  expiresIn: z.number().int().positive().optional(),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type DownloadBody = z.infer<typeof downloadBody>;
export type UploadQuery = z.infer<typeof uploadQuery>;
export type SignUrlBody = z.infer<typeof signUrlBody>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** Shared base for all download responses */
interface DownloadResponseBase {
  success: true;
  download_url: string;
  expires_in: number;
}

/** POST /download (type=workspace) */
export interface DownloadWorkspaceResponse extends DownloadResponseBase {
  summary: {
    organization?: string;
    organizations?: number;
    hubs: number;
    files: number;
  };
}

/** POST /download (type=agent_instructions) */
export interface DownloadAgentInstructionsResponse extends DownloadResponseBase {
  summary: { files: number };
}

/** Union of all download responses */
export type DownloadResponse =
  | DownloadWorkspaceResponse
  | DownloadAgentInstructionsResponse;

/** POST /upload */
export interface UploadResponse {
  success: true;
  file_id: string;
  file_path: string;
}

/** POST /sign */
export interface SignUrlResponse {
  signedUrl: string;
}

// ---------------------------------------------------------------------------
// Serving a stored file
// ---------------------------------------------------------------------------

/** One media type, `type/subtype` in HTTP token characters, then nothing or its parameters. */
const SINGLE_MEDIA_TYPE = /^([!#$%&'*+.^_`|~0-9a-z-]+\/[!#$%&'*+.^_`|~0-9a-z-]+)\s*(?:;|$)/;

/**
 * The type's essence — lowercased, parameters dropped — or `''` when the value is not one plain
 * media type. A browser splits a Content-Type on commas and takes the last type it can parse
 * (Fetch's "extract a MIME type"), so to it `video/mp4;x=1,text/html` is HTML: a value read any
 * other way than it is read here is treated as unknown — sandboxed, and a download.
 */
function essenceOf(contentType: string | null | undefined): string {
  const value = (contentType ?? '').trim().toLowerCase();
  if (value.includes(',')) return '';
  return SINGLE_MEDIA_TYPE.exec(value)?.[1] ?? '';
}

/** Audio or video the browser plays in its media viewer — never an XML-based type. */
function isAudioOrVideo(essence: string): boolean {
  return /^(audio|video)\//.test(essence) && !essence.includes('xml');
}

/** An image the browser draws — never SVG or another XML-based type, which are documents. */
function isDrawnImage(essence: string): boolean {
  return essence.startsWith('image/') && !/svg|xml/.test(essence);
}

/**
 * The Content-Security-Policy of a response that must never act as a document of the origin
 * serving it — every stored file ({@link applyStoredFileHeaders}), and every backend response the
 * web relays onto the app origin. Opened at its URL, it runs no script and loads nothing: a
 * sandboxed document with an opaque origin, so a stored HTML, SVG or XML file never acts as the
 * viewer there. Audio and video alone go unsandboxed, allowed their own origin's media: the
 * browser's media viewer loads the file again from that document, and from an opaque origin the
 * load fails (Chrome refuses it as cross-origin), while a media type never renders as markup.
 * `style-src 'unsafe-inline'` keeps the browser's own viewers laid out (Chrome centers an image
 * with an inline style).
 */
export function inertResponseCsp(contentType: string | null | undefined): string {
  return isAudioOrVideo(essenceOf(contentType))
    ? "default-src 'none'; media-src 'self'; style-src 'unsafe-inline'"
    : "default-src 'none'; style-src 'unsafe-inline'; sandbox";
}

/** Exact types, besides images, audio and video, that a browser shows inertly. */
const INLINE_FILE_TYPES = new Set(['application/pdf', 'text/plain', 'text/markdown', 'application/json']);

/**
 * Whether a stored file of this Content-Type is shown in place when opened at its URL: images
 * (never SVG), audio, video, PDF, plain text, Markdown and JSON. An allowlist — HTML, SVG, XML and
 * every type not named here is a download. The web's previews fetch the bytes and never read it;
 * the mobile app's attachment viewer opens the file at its URL, so a type dropped from this list
 * stops showing there.
 */
function isInlineFileType(contentType: string | null | undefined): boolean {
  const essence = essenceOf(contentType);
  return isDrawnImage(essence) || isAudioOrVideo(essence) || INLINE_FILE_TYPES.has(essence);
}

/**
 * Sets, in place, the headers every stored-file response carries, from its `Content-Type`:
 * {@link inertResponseCsp}, `nosniff`, and a `Content-Disposition` that is `inline` only for
 * {@link isInlineFileType}. `fileName`, when known, rides as the disposition's filename.
 */
export function applyStoredFileHeaders(headers: Headers, fileName?: string): void {
  const contentType = headers.get('Content-Type');
  headers.set('Content-Security-Policy', inertResponseCsp(contentType));
  headers.set('X-Content-Type-Options', 'nosniff');
  const disposition = isInlineFileType(contentType) ? 'inline' : 'attachment';
  headers.set(
    'Content-Disposition',
    fileName ? `${disposition}; filename="${fileName.replace(/["\\\r\n]/g, '_')}"` : disposition,
  );
}
